import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { ErrorBoundary } from "@widgets/common/components/primitives/errorboundary";

import { getS3FileKey, isEmpty } from "base/utils";
import { useAccountStore, useGlobalStore } from "base/stores";

import { ICON_TYPES, DEFAULT_FLOW_ICONS } from "./constant.js";
import { ICON_URL } from "@widgets/inputs/index.jsx";
import { LazyLoadComponent } from "@widgets/others/lazyloader/index.jsx";
import { SvgFromUrl } from "@widgets/common/components/primitives/svg_from_url/index.jsx";
import {
  DEFAULT_ICON_COLOR,
  ICON_COLOR_MAPPING
} from "@widgets/inputs/iconpicker/colors.js";

import styles from "./flowicon.css";

export const FLOW_ICON_SIZES = {
  SIZE64: "size64",
  SIZE54: "size54",
  SIZE48: "size48",
  SIZE40: "size40",
  SIZE36: "size36",
  SIZE32: "size32",
  SIZE24: "size24",
  SIZE28: "size28",
  SIZE20: "size20",
  SIZE16: "size16",
  SIZE12: "size12",
  SIZE100: "size100"
};

FlowIcon.propTypes = {
  defaultIconId: PropTypes.string,
  defaultIconColor: PropTypes.string,
  flowId: PropTypes.string,
  flowType: PropTypes.string,
  size: PropTypes.oneOf(Object.values(FLOW_ICON_SIZES)),
  showBackground: PropTypes.bool,
  editable: PropTypes.bool,
  icon: PropTypes.shape({
    Type: PropTypes.string.isRequired,
    Value: PropTypes.shape({
      Id: PropTypes.string.isRequired,
      Color: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  onUpdate: PropTypes.func,
  iconRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  isCreate: PropTypes.bool,
  showEditAlways: PropTypes.bool,
  className: PropTypes.string
};

export function FlowIcon(props) {
  const {
    defaultIconId,
    defaultIconColor = DEFAULT_ICON_COLOR,
    icon,
    editable,
    showBackground = true,
    size = FLOW_ICON_SIZES.SIZE28,
    flowId,
    flowType,
    onUpdate = () => {},
    iconRef,
    isCreate = false,
    showEditAlways = false,
    className = ""
  } = props;

  const [currentIcon, setCurrentIcon] = useState(icon);
  const CHANNEL_ICON_IMAGEBASEURL =
    useAccountStore.getState().AppConfig?.ProfileBasePath;

  useEffect(
    function updatecurrentIcon() {
      let fetchFlow = useGlobalStore.getState().getFlow(flowId);

      if (flowId && icon === undefined) {
        fetchFlow()
          .then((flow = {}) => {
            setCurrentIcon(flow.Icon);
          })
          .catch((err) => {
            console.info("global store icon issue", err);
          });
      } else {
        setCurrentIcon(icon);
      }
    },
    [icon, flowId]
  );

  const IconComp = useMemo(
    function iconDispacth() {
      const getIcon = () => {
        if (!currentIcon || !currentIcon.Type) {
          if (defaultIconId) {
            return (
              <div
                className={`${styles.iconContainer} ${
                  defaultIconColor && showBackground && styles[defaultIconColor]
                } ${!showBackground && styles.hidebg} ${styles[size]}`}
              >
                <SvgFromUrl
                  url={`${ICON_URL}/${defaultIconId}/${defaultIconId}.svg`}
                  iconColor={ICON_COLOR_MAPPING[defaultIconColor]}
                  fallBackIconColor={"--kf-color-gray-300"}
                />
              </div>
            );
          } else {
            const defaultFlowIcon = DEFAULT_FLOW_ICONS[flowType?.toLowerCase()];
            if (defaultFlowIcon) {
              const { defaultIcon: Icon, defaultColor } = defaultFlowIcon;
              return (
                <div
                  className={`${styles.iconContainer} ${
                    defaultIconColor &&
                    showBackground &&
                    styles[defaultIconColor]
                  } ${styles[size]} ${!showBackground && styles.hidebg}`}
                  style={{
                    backgroundColor: showBackground ? defaultColor : "none"
                  }}
                >
                  <Icon />
                </div>
              );
            }
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          let iconVal = currentIcon.Value;
          if (currentIcon.Type === ICON_TYPES.PRESET) {
            let [id, col] = [
              iconVal?.Id || defaultIconId,
              iconVal?.Color || defaultIconColor
            ];
            return (
              <div
                className={`${styles.iconContainer} ${col && styles[col]} ${
                  !showBackground && styles.hidebg
                } ${styles[size]}  
              `}
              >
                <SvgFromUrl
                  url={`${ICON_URL}/${id}/${id}.svg`}
                  iconColor={
                    col
                      ? ICON_COLOR_MAPPING[col]
                      : ICON_COLOR_MAPPING[defaultIconColor]
                  }
                  fallBackIconColor={"--kf-color-gray-300"}
                />
              </div>
            );
          } else if (currentIcon.Type === ICON_TYPES.CUSTOM) {
            if (iconVal.key) {
              return (
                <img
                  src={iconVal.key}
                  alt={"Custom"}
                  className={`${styles.hidebg} ${styles[size]} ${styles.customimage}`}
                />
              );
            }
            return (
              <img
                src={`${CHANNEL_ICON_IMAGEBASEURL}/${getS3FileKey(
                  iconVal.thumbnail?.key,
                  flowId
                )}`}
                alt={iconVal.original?.name}
                className={`${styles.hidebg} ${styles[size]} ${styles.customimage}`}
              />
            );
          }
        }
      };
      return getIcon;
    },
    [
      CHANNEL_ICON_IMAGEBASEURL,
      currentIcon,
      defaultIconColor,
      defaultIconId,
      flowId,
      flowType,
      showBackground,
      size
    ]
  );

  const onChange = ({ newIcon = {}, isReset = false }) => {
    if (isReset) {
      setCurrentIcon(newIcon);
    } else if (newIcon.Type !== ICON_TYPES.PRESET || newIcon?.Value?.Id) {
      setCurrentIcon(newIcon);
    }
  };

  const onUpdateValue = (newIcon = {}) => {
    if (
      (!isEmpty(newIcon) && newIcon.Type !== ICON_TYPES.PRESET) ||
      newIcon?.Value?.Id
    ) {
      onUpdate(newIcon);
    } else if (isEmpty(newIcon)) {
      onUpdate(null);
    }
  };

  return (
    <ErrorBoundary onError={() => {}}>
      <div className={`${styles.flowicon} ${className}`}>
        {IconComp && IconComp()}
        {editable && (
          <LazyLoadComponent
            component={() => import(/* webpackPreload: true */ "./edit.jsx")}
            componentName={"IconEdit"}
            onChange={onChange}
            onUpdate={onUpdateValue}
            flowId={flowId}
            flowType={flowType}
            defaultIconId={defaultIconId}
            iconRef={iconRef}
            isCreate={isCreate}
            icon={currentIcon?.Value}
            actualIconValue={icon}
            showEditAlways={showEditAlways}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}
